export type JsonTokenKind = "keyword" | "number" | "plain" | "property" | "punctuation" | "string";

export interface JsonToken {
  end: number;
  kind: JsonTokenKind;
  start: number;
  text: string;
}

export interface SnapshotJsonRow {
  propertyToken?: JsonToken;
  start: number;
  tokens: JsonToken[];
}

export interface SnapshotCopyTarget {
  key: string;
  serialize: () => string;
}

export interface SearchMatch {
  end: number;
  index: number;
  start: number;
}

function isJsonPropertyToken(value: string, tokenEnd: number): boolean {
  let cursor = tokenEnd;

  while (cursor < value.length && /\s/.test(value[cursor])) {
    cursor += 1;
  }

  return value[cursor] === ":";
}

export function serializeSnapshotProperty(key: string, value: unknown): string {
  return JSON.stringify({ [key]: value }, null, 2).trim();
}

export function tokenizeJson(value: string): JsonToken[] {
  const pattern = /"(?:\\.|[^"\\])*"|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|\b(?:true|false|null)\b|[{}[\],:]/g;
  const tokens: JsonToken[] = [];
  let cursor = 0;

  for (const match of value.matchAll(pattern)) {
    const start = match.index;
    const text = match[0];

    if (start > cursor) {
      tokens.push({ end: start, kind: "plain", start: cursor, text: value.slice(cursor, start) });
    }

    const end = start + text.length;
    let kind: JsonTokenKind = "punctuation";

    if (text.startsWith('"')) {
      kind = isJsonPropertyToken(value, end) ? "property" : "string";
    } else if (/^-?\d/.test(text)) {
      kind = "number";
    } else if (/^(?:true|false|null)$/.test(text)) {
      kind = "keyword";
    }

    tokens.push({ end, kind, start, text });
    cursor = end;
  }

  if (cursor < value.length) {
    tokens.push({ end: value.length, kind: "plain", start: cursor, text: value.slice(cursor) });
  }

  return tokens;
}

export function getJsonTokenPropertyName(token: Pick<JsonToken, "kind" | "text">): string | undefined {
  if (token.kind !== "property") {
    return undefined;
  }

  try {
    const propertyName = JSON.parse(token.text) as unknown;
    return typeof propertyName === "string" ? propertyName : undefined;
  } catch {
    return undefined;
  }
}

export function isJsonObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function buildSnapshotCopyTargets(value: string, propertyTokens: JsonToken[]): Map<number, SnapshotCopyTarget> {
  const targets = new Map<number, SnapshotCopyTarget>();
  let snapshot: unknown;

  try {
    snapshot = JSON.parse(value) as unknown;
  } catch {
    return targets;
  }

  let propertyTokenIndex = 0;

  const visit = (nestedValue: unknown): void => {
    if (Array.isArray(nestedValue)) {
      nestedValue.forEach(visit);
      return;
    }

    if (!isJsonObject(nestedValue)) {
      return;
    }

    Object.entries(nestedValue).forEach(([key, propertyValue]) => {
      const propertyToken = propertyTokens[propertyTokenIndex];
      propertyTokenIndex += 1;

      if (propertyToken && getJsonTokenPropertyName(propertyToken) === key) {
        targets.set(propertyToken.start, {
          key,
          serialize: () => serializeSnapshotProperty(key, propertyValue),
        });
      }

      visit(propertyValue);
    });
  };

  visit(snapshot);
  return targets;
}

export function splitJsonTokensIntoRows(tokens: JsonToken[]): SnapshotJsonRow[] {
  const rows: SnapshotJsonRow[] = [{ start: 0, tokens: [] }];

  tokens.forEach((token) => {
    let fragmentStart = 0;

    while (fragmentStart <= token.text.length) {
      const newlineIndex = token.text.indexOf("\n", fragmentStart);
      const fragmentEnd = newlineIndex === -1 ? token.text.length : newlineIndex;

      if (fragmentEnd > fragmentStart) {
        const fragment: JsonToken = {
          ...token,
          end: token.start + fragmentEnd,
          start: token.start + fragmentStart,
          text: token.text.slice(fragmentStart, fragmentEnd),
        };
        const row = rows[rows.length - 1];

        row.tokens.push(fragment);

        if (fragment.kind === "property") {
          row.propertyToken = fragment;
        }
      }

      if (newlineIndex === -1) {
        break;
      }

      rows.push({ start: token.start + newlineIndex + 1, tokens: [] });
      fragmentStart = newlineIndex + 1;
    }
  });

  return rows;
}

export function findSearchMatches(value: string, search: string): SearchMatch[] {
  if (!search) {
    return [];
  }

  const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(escapedSearch, "giu");
  const matches: SearchMatch[] = [];

  for (const match of value.matchAll(pattern)) {
    if (match.index === undefined) {
      continue;
    }

    matches.push({
      end: match.index + match[0].length,
      index: matches.length,
      start: match.index,
    });
  }

  return matches;
}

export function findFirstOverlappingMatchIndex(matches: SearchMatch[], tokenStart: number): number {
  let low = 0;
  let high = matches.length;

  while (low < high) {
    const middle = Math.floor((low + high) / 2);

    if (matches[middle].end <= tokenStart) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  return low;
}
